import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core"
import { populateEntityList } from "actions/Clerk/entities"
import { updateAdmin } from "actions/Clerk/users"
import ButtonRounded from "components/Common/ButtonRounded"
import AppContext from "contexts/App"
import CitizenAppContext from "contexts/Citizen/CitizenApp"
import { Form, Formik } from "formik"
import PropTypes from "prop-types"
import { useRef } from "react"
import { useContext, useMemo } from "react"
import InputMask from "react-input-mask"
import { useMutation, useQuery } from "react-query"
import formatPhoneNumber from "utils/formatPhone"
import * as Yup from "yup"

import useStyles from "./styles"

const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/

const schema = Yup.object().shape({
  first_name: Yup.string().required(`Required`),
  last_name: Yup.string().required(`Required`),
  email: Yup.string().email("Please enter a valid email").required(`Required`),
  phone: Yup.string()
    .matches(phoneRegex, "Phone number is not valid")
    .min(14, "Phone number is not valid")
    .max(14, "Phone number is not valid"),
  //14 char - 9 numbers, 3 special chars ()-, 1 space
  admin: Yup.bool(),
  state: Yup.string()
    .nullable(true)
    .when("admin", {
      is: true,
      then: Yup.string().required("Required").typeError("Required"),
    }),
  entity_type: Yup.string()
    .nullable(true)
    .when("admin", {
      is: true,
      then: Yup.string().required("Required").typeError("Required"),
    }),
  entity_id: Yup.number()
    .nullable(true)
    .when("admin", {
      is: true,
      then: Yup.number().required("Required").typeError("Required"),
    }),
})

const UpdateAdminForm = ({ handleClose, user, tableRef }) => {
  const classes = useStyles()
  const formRef = useRef()
  const { openSnackBar, openDialog } = useContext(AppContext)
  const updateAdminMutation = useMutation(updateAdmin)
  const { data: entityPopulateData, isLoading: entityPopulateDataLoading } =
    useQuery("entityPopulateData", populateEntityList)

  const onSubmit = (values) => {
    let data = {
      userId: user?.id,
      ...values,
      phone: values?.phone?.replace(/[^0-9]/g, ""),
    }
    updateAdminMutation
      ?.mutateAsync(data)
      .then((res) => {
        tableRef.current.onQueryChange()
        openSnackBar({ message: "Super admin updated " })
        handleClose()
      })
      .catch((err) => {
        openSnackBar({ message: "Error updating super admin" })
        handleClose()
      })
  }

  const initialValues = useMemo(() => {
    return {
      ...user,
      phone: user?.phone ? formatPhoneNumber(user?.phone) : null,
    }
  }, [user])

  return entityPopulateDataLoading ? (
    <Box display="flex" justifyContent="center">
      <CircularProgress size={18} />
    </Box>
  ) : (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
      innerRef={formRef}
    >
      {({
        handleChange,
        values,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        handleSubmit,
        setErrors,
        setFieldValue,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <DialogContent className={classes.contentContainer}>
              <Typography className={classes.label}>First Name</Typography>
              <TextField
                type="text"
                name="first_name"
                id="first_name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.first_name}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.first_name && errors.first_name}
                error={Boolean(touched.first_name && errors.first_name)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <Typography className={classes.label}>Last Name</Typography>
              <TextField
                type="text"
                name="last_name"
                id="last_name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.last_name}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.last_name && errors.last_name}
                error={Boolean(touched.last_name && errors.last_name)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <Typography className={classes.label}>Phone</Typography>
              <InputMask
                id="phone-mask"
                mask="(999) 999-9999"
                maskChar="_"
                value={values?.phone}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {() => (
                  <TextField
                    type="text"
                    name="phone"
                    id="phone"
                    variant="outlined"
                    fullWidth
                    style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                    helperText={touched.phone && errors.phone}
                    error={Boolean(touched.phone && errors.phone)}
                    FormHelperTextProps={{
                      className: classes.formHelperText,
                    }}
                  />
                )}
              </InputMask>
              <Typography className={classes.label}>Email</Typography>
              <TextField
                type="text"
                name="email"
                id="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.email && errors.email}
                error={Boolean(touched.email && errors.email)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(values?.super_admin)}
                      onChange={(e) => {
                        let data = {
                          action:
                            "to change the super admin privileges of this user?",
                          confirmText: "Yes",
                          cancelText: "No",
                        }
                        openDialog("clickConfirmation", data).then((res) => {
                          setFieldValue("super_admin", !values?.super_admin)
                        })
                      }}
                      name="super_admin"
                    />
                  }
                  label={
                    <Typography className={classes.redText}>
                      {"Super Admin"}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(values?.admin)}
                      onChange={(e) => {
                        let data = {
                          action:
                            "to change the admin privileges of this user?",
                          confirmText: "Yes",
                          cancelText: "No",
                        }
                        openDialog("clickConfirmation", data).then((res) => {
                          if (!values?.admin === false) {
                            setFieldValue("entity_id", null)
                            setFieldValue("entity_type", "")
                            setFieldValue("state", "")
                          }
                          setFieldValue("admin", !values?.admin)
                        })
                      }}
                      name="admin"
                    />
                  }
                  label={
                    <Typography className={classes.redText}>
                      {"Admin"}
                    </Typography>
                  }
                />
                {(values?.admin === 1 || values?.admin === true) && (
                  <>
                    <Typography className={classes.makeAdmin}>
                      {user?.admin
                        ? "Entity's info for this Super Admin"
                        : "Complete the following form to make this super admin an entity's admin"}
                    </Typography>
                    <TextField
                      select
                      name="state"
                      label="State"
                      value={values?.state}
                      data-cy="clerk-request-select-state"
                      onChange={(e) => {
                        handleChange(e)
                        setFieldValue("entity_id", null)
                        setFieldValue("entity_type", "")
                      }}
                      variant="outlined"
                      fullWidth
                      style={{
                        backgroundColor: "#F8F8F8",
                        marginBottom: "25px",
                      }}
                      helperText={touched.state && errors.state}
                      error={Boolean(touched.state && errors.state)}
                      FormHelperTextProps={{
                        "data-cy": "hola",

                        className: classes.formHelperText,
                      }}
                      disabled={values?.admin === 0 || false}
                    >
                      {entityPopulateData?.entities &&
                        Object?.keys(entityPopulateData?.entities)?.length >
                          0 &&
                        Object?.keys(entityPopulateData?.entities)?.map(
                          (state, i) => {
                            return (
                              <MenuItem
                                key={i}
                                value={state}
                                data-cy={`clerk-request-select-state-${state.toLowerCase()}`}
                              >
                                {state}
                              </MenuItem>
                            )
                          }
                        )}
                    </TextField>
                    <TextField
                      select
                      name="entity_type"
                      label="Entity Type"
                      data-cy="clerk-request-select-entity-type"
                      value={values?.entity_type}
                      onChange={(e) => {
                        setFieldValue("entity_id", null)
                        handleChange(e)
                      }}
                      variant="outlined"
                      fullWidth
                      style={{
                        backgroundColor: "#F8F8F8",
                        marginBottom: "25px",
                      }}
                      helperText={touched.entity_type && errors.entity_type}
                      error={Boolean(touched.entity_type && errors.entity_type)}
                      FormHelperTextProps={{
                        className: classes.formHelperText,
                      }}
                      disabled={!values?.state || values?.admin === 0 || false}
                    >
                      {entityPopulateData?.entities &&
                        Object?.values(entityPopulateData?.entities)?.length >
                          0 &&
                        Object.keys(
                          Object.values(entityPopulateData?.entities)[0]
                        ).map((entityType, i) => {
                          return (
                            <MenuItem
                              data-cy={`clerk-request-select-entity-type-${entityType}`}
                              key={i}
                              value={entityType}
                              className={classes.entityTypeText}
                            >
                              <Box className={classes.entityTypeText}>
                                {entityType.charAt(0).toUpperCase() +
                                  entityType.slice(1)}
                              </Box>
                            </MenuItem>
                          )
                        })}
                    </TextField>
                    <TextField
                      select
                      name="entity_id"
                      label="Entity"
                      data-cy="clerk-request-select-entity"
                      value={values?.entity_id}
                      onChange={(e) => {
                        handleChange(e)
                        setFieldValue("entity", e.target.value.entity)
                      }}
                      variant="outlined"
                      fullWidth
                      style={{
                        backgroundColor: "#F8F8F8",
                        marginBottom: "25px",
                      }}
                      helperText={touched.entity_id && errors.entity_id}
                      error={Boolean(touched.entity_id && errors.entity_id)}
                      FormHelperTextProps={{
                        className: classes.formHelperText,
                      }}
                      disabled={
                        !values?.entity_type ||
                        !values?.state ||
                        values?.admin === 0 ||
                        false
                      }
                    >
                      {entityPopulateData?.entities &&
                        Object.values(entityPopulateData?.entities)?.length >
                          0 &&
                        Object.values(entityPopulateData?.entities)[0]
                          [values.entity_type]?.map((x) => x)
                          .map((x, i) => {
                            return (
                              <MenuItem
                                key={x.id}
                                value={x.id}
                                data-cy={`clerk-request-select-entity-${x.entity}`}
                              >
                                {x?.entity}
                              </MenuItem>
                            )
                          })}
                    </TextField>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Box display="flex" justifyContent="center" width="100%">
                <ButtonRounded
                  color="primary"
                  variant="outlined"
                  onClick={handleClose}
                  type="button"
                >
                  Close
                </ButtonRounded>
                <ButtonRounded
                  color="primary"
                  variant="contained"
                  type="submit"
                  className={classes.saveButton}
                  disabled={updateAdminMutation?.isLoading}
                >
                  {updateAdminMutation?.isLoading ? (
                    <Box>
                      <CircularProgress size={15} />
                    </Box>
                  ) : (
                    "Update"
                  )}
                </ButtonRounded>
              </Box>
            </DialogActions>
          </Form>
        )
      }}
    </Formik>
  )
}

UpdateAdminForm.propTypes = {
  handleClose: PropTypes.func,
  user: PropTypes.object,
  getAdminsMutation: PropTypes.func,
  page: PropTypes.number,
  tableRef: PropTypes.object,
}

export default UpdateAdminForm
